import React, { useMemo } from 'react';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import {
  StockAnalysisSectionCode,
  useStockAnalysisModalQuery
} from 'generated/graphql';
import { usePrint } from 'modules/common';
import {
  CustomIcons,
  PrintButton,
  WrappedMarkdown
} from 'modules/components/layer00';
import {
  EpsAndRevenueTable,
  StandardGlAccountsTable
} from 'modules/components/layer25';
import { useParams } from 'react-router-dom';
import { TableOfContents } from './TableOfContents';



export type StockAnalysisModalProps = {
  onClose?: () => void;
};

export const FC = (props: StockAnalysisModalProps) => {
  const {
    isInPrintMode
  } = usePrint();
  const { companyId } = useParams();
  const { data } = useStockAnalysisModalQuery({
    skip: !companyId,
    variables: {
      listedCompanyId: companyId ?? ''
    }
  });

  const stockAnalysis = useMemo(
    () => data?.listedCompany?.stockAnalysis ?? [],
    [data]
  );

  return (
    <Dialog
      open
      fullScreen
      onClose={props.onClose}
    >
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <CustomIcons.LargeIcon.FC icon='COMPANYLOGOFULL' />

          <PrintButton.FC />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant='activityHeader'>
          Investment Case - {data?.listedCompany?.ticker}
        </Typography>

        <Grid
          container
          columnSpacing={4}
          sx={{
            mt: 4
          }}
        >
          {!isInPrintMode && (
            <Grid
              item
              xs={3}
            >
              <TableOfContents
                defaultId='businessModel'
                items={stockAnalysis.map((sa) => ({
                  id: sa.sectionCode,
                  label: sa.title
                })) ?? []}
              />
            </Grid>
          )}

          <Grid
            item
            xs={isInPrintMode ? 12 : 9}
            maxHeight={isInPrintMode ? 'initial' : 'calc(100vh - 150px)'}
            overflow='scroll'
          >
            <Grid
              container
              rowSpacing={4}
            >
              {stockAnalysis.map((topic) => (
                <TopicSection
                  key={topic.id}
                  topic={topic}
                />
              ))}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};



type TopicData = {
  id: string;
  sectionCode: StockAnalysisSectionCode;
  title: string;
  contentMarkdown: string;
};

const TopicSection = ({
  topic
}: {
  topic: TopicData;
}) => {
  return (
    <React.Fragment>
      <Grid
        item
        xs={12}
      >
        <Typography
          variant='h2'
          id={topic.sectionCode}
        >
          {topic.title}
        </Typography>

        <Divider />
      </Grid>

      <TopicSectionContent topic={topic} />
    </React.Fragment>
  );
};



export const TopicSectionContent = ({
  topic
}: {
  topic: TopicData;
}) => {
  const { isInPrintMode } = usePrint();
  const { companyId } = useParams();
  if (topic.sectionCode === 'fundamentals') {
    return (
      <Grid
        item
        xs={12}
      >
        <Typography
          variant='formLabel'
          sx={{
          }}
        >
          More data coming soon!
        </Typography>

        <Box sx={{
          mt: 2
        }}
        >
          <EpsAndRevenueTable.FC
            variant='stockAnalysis'
            companyId={companyId ?? ''}
            maxYearCount={isInPrintMode ? 3 : undefined}
          />
        </Box>
      </Grid>
    );
  }

  if (topic.sectionCode === 'valuationMarketExpectations') {
    return (
      <Box sx={{
        mt: 2
      }}
      >
        <StandardGlAccountsTable.FC
          listedCompanyId={companyId ?? ''}
          accountTypes={[
            'marketcap',
            'pricetobook',
            'pricetoearnings'
          ]}
        />
      </Box>
    );
  }

  return (
    <Grid
      item
      xs={12}
    >
      <WrappedMarkdown.FC
        key={topic.id}
        markdownText={topic.contentMarkdown}
      />
    </Grid>
  );
};
